// src/components/ProjectModal.js
import React, { useEffect } from "react";
import { X, ExternalLink, Github } from "lucide-react";
import { useModal } from "../context/ModalContext";

const ProjectModal = () => {
	const { isOpen, activeProject, closeModal } = useModal();

	// Close modal when Escape key is pressed
	useEffect(() => {
		const handleEscapeKey = (e) => {
			if (e.key === "Escape" && isOpen) {
				closeModal();
			}
		};

		window.addEventListener("keydown", handleEscapeKey);
		return () => window.removeEventListener("keydown", handleEscapeKey);
	}, [isOpen, closeModal]);

	if (!isOpen || !activeProject) return null;

	return (
		<div
			className="fixed inset-0 bg-slate-950/70 backdrop-blur-xs z-50 flex items-center justify-center p-4 overflow-y-auto animate-fade-in"
			onClick={closeModal}
		>
			<div
				className="bg-white rounded-2xl shadow-lift w-full max-w-4xl max-h-[90vh] overflow-y-auto animate-scale-in"
				onClick={(e) => e.stopPropagation()}
			>
				{/* Modal header */}
				<div className="sticky top-0 bg-white/90 backdrop-blur-md p-5 border-b border-slate-200/80 flex justify-between items-center z-10">
					<h2 className="text-2xl font-bold text-slate-900">
						{activeProject.name}
					</h2>
					<button
						onClick={closeModal}
						className="p-1.5 rounded-full hover:bg-slate-100 transition-colors"
						aria-label="Close"
					>
						<X size={22} className="text-slate-500" />
					</button>
				</div>
				{/* Modal content */}
				<div className="p-6">
					{activeProject.image && (
						<div className="w-full h-64 md:h-80 overflow-hidden mb-6 rounded-xl ring-1 ring-slate-200/70">
							<img
								src={activeProject.image}
								alt={activeProject.name}
								className="w-full h-full object-cover"
							/>
						</div>
					)}

					<div className="flex flex-wrap gap-2 mb-6">
						{activeProject.tags.map((tag, i) => (
							<span
								key={i}
								className="bg-brand-50 text-brand-700 ring-1 ring-brand-100 px-3 py-1 rounded-full text-sm font-medium"
							>
								{tag}
							</span>
						))}
					</div>

					{activeProject.longDescription && (
						<div className="max-w-none mb-8">
							<p className="text-lg leading-relaxed text-slate-600">
								{activeProject.longDescription}
							</p>
						</div>
					)}

					{activeProject.features &&
						activeProject.features.length > 0 && (
							<div className="mb-8">
								<h3 className="text-lg font-semibold text-slate-900 mb-4">
									Key Features
								</h3>
								<ul className="space-y-2.5">
									{activeProject.features.map(
										(feature, index) => (
											<li
												key={index}
												className="flex items-start text-slate-600"
											>
												<span className="mt-2 mr-3 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-brand-500" />
												{feature}
											</li>
										)
									)}
								</ul>
							</div>
						)}

					{activeProject.technologies &&
						activeProject.technologies.length > 0 && (
							<div className="mb-8">
								<h3 className="text-lg font-semibold text-slate-900 mb-4">
									Technologies Used
								</h3>
								<div className="flex flex-wrap gap-2">
									{activeProject.technologies.map(
										(tech, index) => (
											<span
												key={index}
												className="bg-slate-100 text-slate-700 ring-1 ring-slate-200/70 px-3 py-1 rounded-full text-sm font-medium"
											>
												{tech}
											</span>
										)
									)}
								</div>
							</div>
						)}

					{/* Links section */}
					{(activeProject.demoUrl || activeProject.githubUrl) && (
						<div className="flex flex-wrap gap-3 mt-8">
							{activeProject.demoUrl && (
								<a
									href={activeProject.demoUrl}
									target="_blank"
									rel="noopener noreferrer"
									className="inline-flex items-center px-4 py-2.5 bg-brand-600 text-white font-medium rounded-xl shadow-xs shadow-brand-600/30 hover:bg-brand-700 hover:-translate-y-0.5 transition-all duration-300"
								>
									<ExternalLink size={18} className="mr-2" />
									View Live Demo
								</a>
							)}
							{activeProject.githubUrl && (
								<a
									href={activeProject.githubUrl}
									target="_blank"
									rel="noopener noreferrer"
									className="inline-flex items-center px-4 py-2.5 bg-slate-900 text-white font-medium rounded-xl hover:bg-slate-800 hover:-translate-y-0.5 transition-all duration-300"
								>
									<Github size={18} className="mr-2" />
									View Source Code
								</a>
							)}
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default ProjectModal;
